/**
 * Select + flatten the read-only snapshot rules that apply to a given approval task,
 * so the Approval Copilot can ground its answers in the same rules the user sees.
 * Mirrors the absence/timecard task detection used by BpmRuleFlowPreview.
 */
import ABSENCE_APPROVAL_RULES_SNAPSHOT from './absenceApprovalRulesSnapshot';
import { TIMECARD_RULESETS, APPROVER_PALETTE } from './timecardApprovalRulesSnapshot';

const PALETTE_LABEL = Object.fromEntries((APPROVER_PALETTE || []).map((p) => [p.type, p.label]));

function summarizeSpec(spec) {
  if (!spec || typeof spec !== 'object') return '';
  const bits = [];
  if (spec.listBuilder) bits.push(`list builder: ${spec.listBuilder}`);
  if (spec.responseType) bits.push(`response: ${spec.responseType}`);
  if (spec.levels != null && spec.levels !== '') bits.push(`${spec.levels} level(s)`);
  if (spec.hierarchyType) bits.push(`hierarchy: ${spec.hierarchyType}`);
  if (spec.startingParticipant) bits.push(`start: ${spec.startingParticipant}`);
  if (spec.topParticipant) bits.push(`top: ${spec.topParticipant}`);
  if (spec.autoActionEnabled) bits.push(`auto-action: ${spec.autoAction || 'enabled'}`);
  return bits.join('; ');
}

function flattenRule(r, task) {
  const thenRefs = Array.isArray(r.then) ? r.then.map((t) => PALETTE_LABEL[t] || t) : [];
  const thenDetail = r.thenSpec ? summarizeSpec(r.thenSpec) : r.thenDetail || '';
  return {
    name: r.name,
    ifText: r.ifText || '',
    thenRefs,
    thenDetail: Array.isArray(thenDetail) ? thenDetail.join('; ') : thenDetail,
    autoApprove: r.autoApprove === true,
    ruleset: task?.name,
  };
}

/**
 * Scopes the Approval Copilot can ground on when it is not opened from a specific
 * task (e.g. on the standalone Approval Copilot page). Derived from the snapshots.
 */
export function listSnapshotScopes() {
  const scopes = [];
  const absence = ABSENCE_APPROVAL_RULES_SNAPSHOT.task;
  if (absence) {
    scopes.push({
      id: 'absence',
      label: absence.name,
      taskKey: absence.taskKey,
      name: absence.name,
      category: 'absence',
    });
  }
  const tc = (TIMECARD_RULESETS || [])[0]?.task;
  if (tc) {
    scopes.push({
      id: 'time_labor',
      label: 'Timecard Approval',
      taskKey: tc.taskKey,
      name: tc.name,
      category: 'time_labor',
    });
  }
  return scopes;
}

export function detectTaskKind({ taskKey, name, category } = {}) {
  const categoryStr = String(category || '');
  const nameStr = String(name || '');
  const taskKeyStr = String(taskKey || '');
  const isTimecard =
    /time|labor/i.test(categoryStr) ||
    /time\s*card|timecard|payroll\s*time/i.test(nameStr) ||
    /time-?card/i.test(taskKeyStr) ||
    (TIMECARD_RULESETS || []).some((rs) => rs.task.taskKey === taskKey);
  const isAbsence =
    !isTimecard &&
    (taskKey === ABSENCE_APPROVAL_RULES_SNAPSHOT.task.taskKey ||
      /absence|leave/i.test(categoryStr) ||
      /absence|leave/i.test(nameStr));
  if (isTimecard) return 'time_labor';
  if (isAbsence) return 'absence';
  return 'unknown';
}

/**
 * @returns {{ kind: 'time_labor'|'absence'|'unknown', rules: object[] }}
 */
export function selectSnapshotRulesForTask({ taskKey, name, category } = {}) {
  const kind = detectTaskKind({ taskKey, name, category });

  if (kind === 'time_labor') {
    const rules = [];
    (TIMECARD_RULESETS || []).forEach((rs) => {
      (rs.rules || []).forEach((r) => rules.push(flattenRule(r, rs.task)));
    });
    return { kind, rules };
  }

  if (kind === 'absence') {
    return {
      kind,
      rules: (ABSENCE_APPROVAL_RULES_SNAPSHOT.rules || []).map((r) =>
        flattenRule(r, ABSENCE_APPROVAL_RULES_SNAPSHOT.task)
      ),
    };
  }

  return { kind, rules: [] };
}
